import { Button, Checkbox, TextField, useAlert } from '@ads/components-react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SerieVideoViewModel } from '@/shared/api/generated';
import { DatePicker, Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { useSaveSerieVideo } from '../../../hooks/useSaveSerieVideo';
import { type SerieVideoFormValues, serieVideoFormSchema } from '../../../types/serieVideoForm';

export interface SerieVideoFormProps {
  readonly serieId: number;
  /** Vídeo em edição; `null` para criação. */
  readonly video: SerieVideoViewModel | null;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

const DEFAULT_VALUES: SerieVideoFormValues = {
  title: '',
  date: '',
  description: '',
  channel: '',
  thumbnailFile: null,
  removeThumbnail: false,
  active: true,
};

/**
 * Formulário de Vídeo (criar/editar), usado em tela dedicada (não modal). Um
 * único formulário cobre os dois modos: `video === null` abre vazio (com
 * `Ativo` ligado); caso contrário, é pré-preenchido a partir do
 * `SerieVideoViewModel`. Fiel aos frames *Vídeo · Criar/Editar* de
 * `design/series.pen`, incluindo os botões soltos abaixo do card.
 */
export function SerieVideoForm({ serieId, video, onSaved, onCancel }: SerieVideoFormProps) {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const { save, isPending } = useSaveSerieVideo(serieId);
  const fileInputId = useId();

  const isEdit = video?.id != null;
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SerieVideoFormValues>({
    resolver: zodResolver(serieVideoFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    reset(
      video
        ? {
            title: video.title ?? '',
            date: video.date ? video.date.slice(0, 10) : '',
            description: video.resumee ?? '',
            channel: video.embed ?? '',
            thumbnailFile: null,
            removeThumbnail: false,
            active: video.active ?? true,
          }
        : DEFAULT_VALUES,
    );
  }, [video, reset]);

  const thumbnailFile = watch('thumbnailFile');
  const removeThumbnail = watch('removeThumbnail');

  useEffect(() => {
    if (!thumbnailFile) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const existingThumbnailUrl =
    isEdit && video?.thumbnail && !removeThumbnail ? video.thumbnail : undefined;
  const displayedThumbnailUrl = previewUrl ?? existingThumbnailUrl;

  const onSubmit = handleSubmit(values => {
    save(values, video?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'videos.toast.updateSuccess' : 'videos.toast.createSuccess'),
        });
        onSaved();
      },
      onError: () => alert({ alertType: 'warning', title: t('videos.toast.saveError') }),
    });
  });

  return (
    <form className="flex flex-col gap-xs" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
        <h2 className="ts-heading-sm text-ctx-content-title">
          {t(isEdit ? 'videos.form.editTitle' : 'videos.form.createTitle')}
        </h2>

        <div className="grid grid-cols-1 gap-mini sm:grid-cols-[1fr_200px]">
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TextField
                label={`${t('videos.form.title')}*`}
                placeholder={t('videos.form.titlePlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                size="md"
                fullWidth
                hasError={Boolean(errors.title)}
                {...(errors.title ? { helper: t('videos.form.errors.titleRequired') } : {})}
              />
            )}
          />
          <Controller
            control={control}
            name="date"
            render={({ field }) => (
              <DatePicker
                label={`${t('videos.form.date')}*`}
                value={field.value}
                onChange={field.onChange}
                size="md"
                hasError={Boolean(errors.date)}
                {...(errors.date ? { helper: t('videos.form.errors.dateRequired') } : {})}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <FormField
              label={`${t('videos.form.description')}*`}
              {...(errors.description
                ? { error: t('videos.form.errors.descriptionRequired') }
                : {})}
            >
              <Textarea
                placeholder={t('videos.form.descriptionPlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                rows={4}
              />
            </FormField>
          )}
        />

        <Controller
          control={control}
          name="channel"
          render={({ field }) => (
            <TextField
              label={`${t('videos.form.channel')}*`}
              placeholder={t('videos.form.channelPlaceholder')}
              value={field.value}
              onChange={e => field.onChange(e.target.value)}
              size="md"
              fullWidth
              hasError={Boolean(errors.channel)}
              {...(errors.channel ? { helper: t('videos.form.errors.channelRequired') } : {})}
            />
          )}
        />

        <div className="flex flex-col gap-nano">
          <span className="ts-subtitle-xs text-ctx-content-title">
            {t('videos.form.thumbnail')}
          </span>
          {displayedThumbnailUrl ? (
            <div className="flex flex-col items-start gap-nano">
              <img
                src={displayedThumbnailUrl}
                alt={t('videos.form.thumbnail')}
                className="h-[140px] w-[200px] rounded-sm object-cover"
              />
              {isEdit && (
                <Button
                  hierarchy="danger"
                  size="md"
                  type="button"
                  onClick={() => {
                    setValue('thumbnailFile', null);
                    setValue('removeThumbnail', true);
                  }}
                >
                  {t('videos.form.removePhoto')}
                </Button>
              )}
            </div>
          ) : (
            <label
              htmlFor={fileInputId}
              className="flex h-[112px] w-[200px] cursor-pointer items-center justify-center rounded-sm border-hairline border-ctx-layout-border bg-base-gray-100"
            >
              <PhotoIcon className="h-xxl w-xxl text-ctx-content-placeholder" />
            </label>
          )}
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={e => {
              const picked = e.target.files?.[0];
              if (picked) {
                setValue('thumbnailFile', picked);
                setValue('removeThumbnail', false);
              }
              e.target.value = '';
            }}
          />
        </div>

        <Controller
          control={control}
          name="active"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              size="md"
              label={t('videos.form.active')}
              onCheckedChange={checked => {
                if (checked !== 'indeterminate') field.onChange(checked);
              }}
            />
          )}
        />
      </div>

      <div className="flex items-center justify-end gap-nano">
        <Button hierarchy="secondary" size="md" type="button" onClick={onCancel}>
          {t('videos.form.cancel')}
        </Button>
        <Button
          hierarchy="primary"
          size="md"
          type="submit"
          isLoading={isPending}
          disabled={isPending}
        >
          {t('videos.form.save')}
        </Button>
      </div>
    </form>
  );
}
